import React from "react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorCount: number;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private autoRecoverTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  // NOTE: Δεν βάζουμε ποτέ noindex σε προσωρινό crash — το Googlebot μπορεί να
  // δει το transient error state και να αποδεικτοποιήσει (deindex) τη σελίδα.
  private addNoIndexMeta() {
    // intentionally no-op (SEO safety)
  }

  private removeNoIndexMeta() {
    if (typeof document === "undefined") return;
    // Καθάρισε τυχόν παλιό tag από προηγούμενες εκδόσεις
    const tag = document.querySelector('meta[name="robots"][data-error-boundary="1"]');
    if (tag) tag.remove();
  }


  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[ErrorBoundary] Caught:", error, errorInfo);

    const newCount = this.state.errorCount + 1;
    this.setState({ errorCount: newCount });
    this.addNoIndexMeta();

    // Auto-recover after 2s if fewer than 3 consecutive crashes
    if (newCount < 3) {
      this.autoRecoverTimer = setTimeout(() => {
        this.removeNoIndexMeta();
        this.setState({ hasError: false, error: null });
      }, 2000);
    }
  }

  componentWillUnmount() {
    if (this.autoRecoverTimer) clearTimeout(this.autoRecoverTimer);
    this.removeNoIndexMeta();
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const isCritical = this.state.errorCount >= 3;

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="text-center max-w-md space-y-4">
            <div className="text-5xl">{isCritical ? "😵" : "⏳"}</div>
            <h1 className="text-xl font-bold text-foreground">
              {isCritical ? "Κάτι πήγε στραβά" : "Μισό λεπτό..."}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isCritical
                ? "Προέκυψε επαναλαμβανόμενο σφάλμα. Δοκίμασε να ανανεώσεις τη σελίδα."
                : "Γίνεται αυτόματη ανάκτηση, περίμενε..."}
            </p>
            {isCritical && (
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
              >
                Ανανέωση σελίδας
              </button>
            )}
            {this.state.error && isCritical && (
              <details className="text-xs text-muted-foreground mt-4 text-left">
                <summary className="cursor-pointer">Τεχνικές λεπτομέρειες</summary>
                <pre className="mt-2 p-2 bg-muted rounded text-[10px] overflow-auto max-h-32">
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
